<template>
  <div class="ds-tooltip" data-testid="ds-tooltip">
    <Float
      :transition-name="transitionName"
      :show="isShowing"
      :placement="placement"
      :offset="offset"
      :shift="6"
      :flip="10"
      :arrow="arrow"
      :z-index="zIndex"
      :portal="portal"
      composable
    >
      <FloatReference>
        <div
          class="ds-tooltip-activator"
          data-testid="ds-tooltip-activator"
          @mouseenter="onMouseenter"
          @mouseleave="onMouseleave"
        >
          <slot name="activator"></slot>
        </div>
      </FloatReference>
      <FloatContent>
        <div
          data-testid="ds-tooltip-float-content"
          :class="[
            'ds-tooltip-float-content',
            opacityClass,
            themeColorClass,
            sizeClass,
            contentClass,
          ]"
          @mouseenter="onMouseenter"
          @mouseleave="onMouseleave"
        >
          <template v-if="arrow">
            <div class="ds-tooltip-float-content-arrow-wrapper"></div>
            <FloatArrow
              data-testid="ds-tooltip-float-content-arrow"
              class="ds-tooltip-float-content-arrow"
            />
          </template>
          <div
            :class="['ds-tooltip-float-content-container', textAlignmentClass]"
            role="tooltip"
            data-testid="ds-tooltip-float-content-container"
          >
            <slot name="content">
              {{ content }}
            </slot>
          </div>
        </div>
      </FloatContent>
    </Float>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import {
  Float,
  FloatArrow,
  FloatContent,
  FloatReference,
} from "@headlessui-float/vue";
import type { PropType } from "vue";

import type {
  DsOpacity,
  DsPlacement,
  DsTextAlignment,
  DsThemeSize,
} from "../../types";
import type { DsTooltipThemeColor } from "./DsTooltip.types";

defineOptions({
  name: "DsTooltip",
});

const props = defineProps({
  content: {
    type: String,
    required: false,
  },
  placement: {
    type: String as PropType<DsPlacement>,
    default: "top",
  },
  themeColor: {
    type: String as PropType<DsTooltipThemeColor>,
    default: "black",
  },
  size: {
    type: String as PropType<DsThemeSize>,
    default: "md",
  },
  arrow: {
    type: Boolean,
    default: true,
  },
  zIndex: {
    type: Number,
    default: 10,
  },
  openDelay: {
    type: Number,
    default: 0,
  },
  closeDelay: {
    type: Number,
    default: 50,
  },
  portal: {
    type: Boolean,
    default: false,
  },
  textAlignment: {
    type: String as PropType<DsTextAlignment>,
    default: "left",
  },
  opacity: {
    type: String as PropType<DsOpacity>,
    default: "100",
  },
  contentCssClass: {
    type: String,
    required: false,
  },
  offset: {
    type: Number,
    default: 10,
  },
  transitionName: {
    type: String,
    default: "",
  },
});

const isShowing = ref(false);
const openTimer = ref(0);
const closeTimer = ref(0);

const opacityClass = computed((): string => {
  if (props.opacity) {
    return `ds-tooltip-float-content-opacity-${props.opacity}`;
  }

  return "";
});

const sizeClass = computed((): string => {
  if (props.contentCssClass === undefined) {
    return `ds-tooltip-float-content-${props.size}`;
  }
  return "";
});

const themeColorClass = computed((): string => {
  return `ds-tooltip-float-content-${props.themeColor}`;
});

const textAlignmentClass = computed((): string => {
  return `ds-tooltip-float-content-container-text-${props.textAlignment}`;
});

const contentClass = computed((): string => {
  if (props.contentCssClass) {
    return props.contentCssClass;
  }
  return "";
});

function onMouseenter() {
  if (closeTimer.value) {
    clearTimeout(closeTimer.value);
  }

  openTimer.value = window.setTimeout(() => {
    isShowing.value = true;
  }, props.openDelay);
}

function onMouseleave() {
  if (openTimer.value) {
    clearTimeout(openTimer.value);
  }

  closeTimer.value = window.setTimeout(() => {
    isShowing.value = false;
  }, props.closeDelay);
}

defineExpose({
  isShowing,
});
</script>

<style lang="scss" scoped>
.ds-tooltip {
  @apply flex;

  &-activator {
    @apply inline-flex items-center cursor-pointer;
  }

  &-float-content {
    box-shadow:
      0 2px 10px 0 rgba(0, 0, 0, 0.04),
      0 4px 25px 0 rgba(0, 0, 0, 0.1);

    &-xxs {
      @apply max-w-[5rem];
    }

    &-xs {
      @apply max-w-[7rem];
    }

    &-sm {
      @apply max-w-[8rem];
    }

    &-md {
      @apply max-w-[12rem];
    }

    &-lg {
      @apply max-w-[13rem];
    }

    &-xl {
      @apply max-w-[15rem];
    }

    &-xxl {
      @apply max-w-[20rem];
    }

    $opacities: 5, 10, 20, 25, 30, 40, 50, 60, 70, 75, 80, 90, 100;
    @each $opacity in $opacities {
      &-opacity-#{$opacity} {
        @apply opacity-#{$opacity};
      }
    }

    &-black {
      .ds-tooltip-float-content-container {
        @apply bg-gray-900 text-white;
      }

      .ds-tooltip-float-content-arrow {
        @apply bg-gray-900;
      }
    }

    &-white {
      .ds-tooltip-float-content-container {
        @apply bg-white text-gray-900;
      }

      .ds-tooltip-float-content-arrow {
        @apply bg-white;
      }
    }

    &-arrow-wrapper {
      display: block;
      height: calc(100% + 20px);
      left: -10px;
      position: absolute;
      top: -10px;
      width: calc(100% + 20px);
      z-index: -1;
    }

    &-arrow {
      @apply absolute w-6 h-6 rotate-45 rounded-md;
    }

    &-container {
      @apply rounded-md shadow p-3 font-normal text-xs relative break-words;

      &-text {
        &-left {
          @apply text-left;
        }

        &-right {
          @apply text-right;
        }

        &-center {
          @apply text-center;
        }
      }
    }
  }
}
</style>
